import { inject, Injectable } from "@angular/core";
import { FgdsProductType, Product } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { GasWarningCenterConfiguration } from "@domain/project/configurations/gas-warning-center-configuration";
import { AttachmentDataService } from "@domain/project/product-data/attachment-data.service";
import { ProductData } from "@domain/project/product-data/product-data";
import { ConfigProductMap } from "@domain/project/product-list/config-product-map";
import { ProductListDiscountSlot } from "@lib/dtos/project/product-list-discount.dto";
import { ProductWithQuantity } from "@project/gas-warning-center-configuration/product-with-quantity";
import { ProductItem } from "@project/product-item";
import { ALPHABET } from "@utils/alphabet";
import { catchError, defaultIfEmpty, forkJoin, map, Observable, of } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class GasWarningCenterDataService {
  private productService = inject(ProductService);
  private attachmentDataService = inject(AttachmentDataService);

  getGasWarningCenterData(
    gasWarningCenters: readonly GasWarningCenterConfiguration[],
    localeId: string,
  ): Observable<ProductData[][]> {
    if (gasWarningCenters.length === 0) {
      return of([]);
    }
    return forkJoin(gasWarningCenters.map((gasWarningCenter) => this.getProducts(gasWarningCenter, localeId)));
  }

  assignProductsToConfiguration(
    configs: readonly GasWarningCenterConfiguration[],
    uniqueConfigs: GasWarningCenterConfiguration[],
    localeId: string,
  ): Observable<ConfigProductMap[]> {
    if (uniqueConfigs.length === 0) {
      return of([]);
    }
    uniqueConfigs.sort((a, b) => parseInt(a.positionNumber!) - parseInt(b.positionNumber!));
    return forkJoin(
      uniqueConfigs.map((uniqueConfig) =>
        this.getProducts(uniqueConfig, localeId).pipe(
          map((productData) => {
            const placedProductsCount = configs.filter((config) => config.id === uniqueConfig.id).length;
            return new ConfigProductMap(uniqueConfig, productData, placedProductsCount);
          }),
        ),
      ),
    );
  }

  private getProducts(gasWarningCenterConfig: GasWarningCenterConfiguration, localeId: string): Observable<ProductData[]> {
    let suffixCounter = 0;

    const setSuffix = (items: ProductData[]) => {
      items.forEach((pd) => this.setPositionId(pd, gasWarningCenterConfig, suffixCounter++));
      return items;
    };

    const getAttachments$ = (attachments: ProductItem[]) =>
      this.attachmentDataService.getProductData(gasWarningCenterConfig, attachments, localeId);

    if (!gasWarningCenterConfig.regard3000Configuration && !gasWarningCenterConfig.regard7000PlusConfiguration) {
      const gasWarningCenter$ = this.productService.getProductById(gasWarningCenterConfig.productId).pipe(
        map((product) => {
          if (!product || product.type !== FgdsProductType.GASWARNINGCENTER) return null;
          const pd = ProductData.create(
            product,
            localeId,
            this.productService.getFilterCountry()!,
            1,
            ProductListDiscountSlot.MAIN,
          );
          pd.positionId = gasWarningCenterConfig.positionNumber!;
          return pd;
        }),
        catchError(() => of(null)),
      );

      return forkJoin([gasWarningCenter$, getAttachments$(gasWarningCenterConfig.attachments).pipe(map(setSuffix))]).pipe(
        map(([center, attachments]) => [center, ...attachments].filter((p): p is ProductData => !!p)),
      );
    }

    if (gasWarningCenterConfig.regard3000Configuration) {
      const regard3000Config = gasWarningCenterConfig.regard3000Configuration;
      return forkJoin({
        baseUnits: this.getProductData(
          gasWarningCenterConfig,
          regard3000Config.baseUnits,
          0,
          localeId,
          ProductListDiscountSlot.REGARD_BASE_UNIT,
        ),
        lidUnits: this.getProductData(
          gasWarningCenterConfig,
          regard3000Config.lidUnits ?? [],
          regard3000Config.baseUnits.length,
          localeId,
          ProductListDiscountSlot.REGARD_LID_UNIT,
        ),
        inputModules: this.getProductData(
          gasWarningCenterConfig,
          regard3000Config.inputModules ?? [],
          0,
          localeId,
          ProductListDiscountSlot.REGARD_INPUT_MODULE,
        ),
        relayModules: this.getProductData(
          gasWarningCenterConfig,
          regard3000Config.relayModules ?? [],
          0,
          localeId,
          ProductListDiscountSlot.REGARD_RELAY_MODULE,
        ),
        gatewayModules: this.getProductData(
          gasWarningCenterConfig,
          regard3000Config.gatewayModules ?? [],
          0,
          localeId,
          ProductListDiscountSlot.REGARD_GATEWAY_MODULE,
        ),
        slotCovers: regard3000Config.slotCovers
          ? this.getProductData(
              gasWarningCenterConfig,
              [regard3000Config.slotCovers],
              0,
              localeId,
              ProductListDiscountSlot.REGARD_SLOT_COVER,
            )
          : of([]),
        attachments: getAttachments$(gasWarningCenterConfig.attachments),
      }).pipe(
        map(({ baseUnits, lidUnits, inputModules, relayModules, gatewayModules, slotCovers, attachments }) => {
          const regard3000Products = [
            ...baseUnits,
            ...lidUnits,
            ...inputModules,
            ...relayModules,
            ...gatewayModules,
            ...slotCovers,
          ];
          const ordered = [...regard3000Products, ...attachments];
          ordered.forEach((pd, index) => this.setPositionId(pd, gasWarningCenterConfig, index));
          return ordered;
        }),
      );
    }

    const regard7000Config = gasWarningCenterConfig.regard7000PlusConfiguration!;
    return forkJoin({
      dockingStations: this.getProductData(
        gasWarningCenterConfig,
        regard7000Config.dockingStations,
        suffixCounter,
        localeId,
        ProductListDiscountSlot.REGARD7000_DOCKING_STATION,
      ),
      inputModules: this.getProductData(
        gasWarningCenterConfig,
        regard7000Config.inputModules,
        suffixCounter,
        localeId,
        ProductListDiscountSlot.REGARD7000_INPUT_MODULE,
      ),
      relayModules: this.getProductData(
        gasWarningCenterConfig,
        regard7000Config.relayModules,
        suffixCounter,
        localeId,
        ProductListDiscountSlot.REGARD7000_RELAY_MODULE,
      ),
      gatewayModules: this.getProductData(
        gasWarningCenterConfig,
        regard7000Config.gatewayModules,
        suffixCounter,
        localeId,
        ProductListDiscountSlot.REGARD7000_GATEWAY_MODULE,
      ),
      terminalBlocks: this.getProductData(
        gasWarningCenterConfig,
        regard7000Config.terminalBlocks,
        suffixCounter,
        localeId,
        ProductListDiscountSlot.REGARD7000_TERMINAL_BLOCK,
      ),
      dashboards: this.getProductData(
        gasWarningCenterConfig,
        regard7000Config.dashboards,
        suffixCounter,
        localeId,
        ProductListDiscountSlot.REGARD7000_DASHBOARD,
      ),
      attachments: getAttachments$(gasWarningCenterConfig.attachments),
    }).pipe(
      map(({ dockingStations, inputModules, relayModules, gatewayModules, terminalBlocks, dashboards, attachments }) => [
        ...[dockingStations, inputModules, relayModules, gatewayModules, terminalBlocks, dashboards]
          .flat()
          .map((product, index) => {
            this.setPositionId(product, gasWarningCenterConfig, suffixCounter + index);
            return product;
          }),
        ...attachments.map((product, index) => {
          this.setPositionId(
            product,
            gasWarningCenterConfig,
            suffixCounter +
              dockingStations.length +
              inputModules.length +
              relayModules.length +
              gatewayModules.length +
              terminalBlocks.length +
              dashboards.length +
              index,
          );
          return product;
        }),
      ]),
    );
  }

  private getProductData(
    gasWarningCenterConfig: GasWarningCenterConfiguration,
    productItems: ProductItem[],
    positionSuffix: number,
    localeId: string,
    discountSlot: ProductListDiscountSlot,
  ): Observable<ProductData[]> {
    if (!productItems.length) {
      return of([]);
    }

    return this.getProductsByIds(this.getProductIds(productItems)).pipe(
      map((products) => {
        const productsWithQuantity = products.reduce((acc, product, index) => {
          acc.push({
            product,
            quantity: productItems[index].quantity,
          });
          return acc;
        }, [] as ProductWithQuantity[]);

        const productData = productsWithQuantity.map((productWithQuantity) => {
          return ProductData.create(
            productWithQuantity.product,
            localeId,
            this.productService.getFilterCountry()!,
            productWithQuantity.quantity,
            discountSlot,
          );
        });
        productData.forEach((item, index) => this.setPositionId(item, gasWarningCenterConfig, positionSuffix + index));
        return productData;
      }),
      catchError(() => of([])),
    );
  }

  public getProductIds(products: ProductItem[]) {
    return products.map((product) => product.productId);
  }

  private setPositionId(productData: ProductData, gasWarningCenterConfig: GasWarningCenterConfiguration, positionSuffix: number) {
    productData.positionId = gasWarningCenterConfig.positionNumber! + "." + ALPHABET[positionSuffix];
  }

  private getProductsByIds(productIds: string[]): Observable<Product[]> {
    if (!productIds.length) {
      return of([]);
    }

    const products = productIds.map((productId) =>
      this.productService.getProductById(productId).pipe(
        map((product) =>
          product && (product.type === FgdsProductType.GASWARNINGCENTER || product.type === FgdsProductType.ATTACHMENT)
            ? product
            : null,
        ),
        defaultIfEmpty(undefined),
        catchError(() => of(undefined)),
      ),
    );

    return forkJoin(products).pipe(map((results) => results.filter((p): p is Product => !!p)));
  }
}
